// @flow
import * as React from "react";
import { connect, type MapStateToProps } from "react-redux";
import idx from "idx";
import styled from "styled-components";
import Gettext from "../../common/Gettext";
import RawScrollable from "../../common/Scrollable";
import RawLink from "../Link";
import Card from "../Card";
import Spinner from "../../Spinners/SimpleSpinner";
import TournamentAPI from "../Api";
// import { disableRefill } from "../helpers/refill";
import { Bar as RawBar } from "../styles";
import { rtl } from "../../common/styled-mixins";

import type { State as ReduxState } from "../../../redux/reducer";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 100%;
  white-space: normal;
  overflow: hidden;
`;

const Info = styled.div`
  padding: 40px 0;
  text-align: center;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 15px;
  overflow: hidden;

  @media (max-width: 959px) {
    padding: 15px 10px;
  }

  @media (min-width: 960px) {
    ${rtl("padding-right", "3px")};
  }
`;

const Cards = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  align-content: flex-start;
  flex: 1;

  @media (min-width: 960px) {
    margin-bottom: 1em;
    padding: 0 15px;
  }

  @media (max-width: 959px) {
    flex-direction: column;
    justify-content: center;
    align-content: center;
    align-items: center;
    padding: 0 16px;
  }
`;

const Bar = styled(RawBar)`
  margin-bottom: 1em;
`;

const Link = styled(RawLink)`
  margin-top: 0;

  @media (min-width: 960px) {
    ${rtl("padding-right", "15px")};
  }
`;

const Scrollable = styled(RawScrollable)`
  flex-grow: 2;

  @media (min-width: 960px) {
    :after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1em;
      background: linear-gradient(
        to bottom,
        ${process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
            ? "rgba(49,49,49,0)"
            : "rgba(60,52,70,0)"}
          0%,
        ${({ theme }) => theme.mainHighlight} 100%
      );
    }
  }

  & + ${Link} {
    margin-top: 15px;
  }
`;

const Category = styled.div`
  margin-bottom: 8px;
`;

const CategoryTitle = styled.div`
  margin: 16px 10px;
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.tabsTournamentsCategoryTitle};
  text-transform: uppercase;
  text-align: center;
`;

type SharedProps = {
  onClose: () => void,
  dispatch: Function,
  enabledStock: boolean
};

type Props = {
  ...SharedProps,
  dispatch: Function,
  api: any
};

class TabContents extends React.Component<Props, {}> {
  onActivateTournament = async id => {
    const { api, onClose, dispatch } = this.props;

    const activate = await api.fetchEntity(
      `/api/tournaments/activate?id=${id}`
    );

    if (activate !== null) {
      api.fetchEntity(`/api/tournaments/list?status=1`);
      api.fetchEntity(`/api/tournaments/list?status=1&participation_status=1`);
      api.fetchEntity(`/api/tournaments/info?id=${id}`);
      await api.fetchEntity(`/api/tournaments/stats`);
      dispatch({
        type: "ENGAGE_TOURNAMENT_MODE"
      });
      onClose();
    }
  };

  componentDidMount() {
    const { api } = this.props;
    api.fetchEntity(`/api/tournaments/stats`);
    api.fetchEntity(`/api/tournaments/list?limit=100&offset=0&status=1`);
    api.fetchEntity(
      `/api/tournaments/list?limit=100&offset=0&status=1&participation_status=1`
    );
  }

  render() {
    const { api, enabledStock, onClose } = this.props;

    const stats = idx(
      api.readEntity(`/api/tournaments/stats`),
      stats => stats.value
    );
    const current_tournament = idx(stats, stats => stats.active_tournament);
    const participated_tournaments = idx(
      api.readEntity(
        `/api/tournaments/list?limit=100&offset=0&status=1&participation_status=1`
      ),
      tournaments => tournaments.value
    );
    const active_tournaments = idx(
      api.readEntity(`/api/tournaments/list?limit=100&offset=0&status=1`),
      tournaments => tournaments.value
    );

    if (
      active_tournaments == null ||
      participated_tournaments == null ||
      stats == null
    ) {
      return (
        <Info>
          <Spinner accent />
        </Info>
      );
    }

    if (active_tournaments != null && active_tournaments.count < 1) {
      return (
        <Container>
          <Wrapper>
            <Info>
              <Gettext>There are no tournaments available.</Gettext>
            </Info>
            <Link to="/tournaments" target="_self">
              <Gettext>All tournaments</Gettext>
            </Link>
          </Wrapper>
        </Container>
      );
    }

    let excludeIds =
      (participated_tournaments &&
        participated_tournaments.items.reduce((res, item) => {
          res.push(item.id);
          return res;
        }, [])) ||
      [];
    let nearest = [];
    let nearestPaid = active_tournaments.items.find(
      item =>
        item.participation_status === 0 &&
        item.type === "pay" &&
        item.start_date * 1000 > this.props.time &&
        !excludeIds.includes(item.id)
    );
    let nearestFree = active_tournaments.items.find(
      item =>
        item.participation_status === 0 &&
        item.type === "free" &&
        item.start_date * 1000 > this.props.time &&
        !excludeIds.includes(item.id)
    );

    if (nearestPaid) {
      nearest.push(nearestPaid);
    }

    if (nearestFree) {
      nearest.push(nearestFree);
    }

    return (
      <Container>
        <Scrollable bar={<Bar />}>
          {participated_tournaments.items.length > 0 && (
            <React.Fragment>
              <Category>
                <CategoryTitle>
                  <Gettext>You are participating</Gettext>
                </CategoryTitle>
                <Cards>
                  {participated_tournaments.items
                    .filter(item => item.participation_status === 1)
                    .map(tournament => {
                      return (
                        <Card
                          key={tournament.id}
                          layout="horizontal"
                          onClose={onClose}
                          isCurrent={
                            tournament.id ===
                            (current_tournament && current_tournament.id)
                          }
                          onActivateTournament={this.onActivateTournament}
                          enabledStock={enabledStock}
                          {...tournament}
                        />
                      );
                    })}
                </Cards>
              </Category>
            </React.Fragment>
          )}
          {nearest.length > 0 && (
            <React.Fragment>
              <Category>
                <CategoryTitle>
                  <Gettext>The nearest tournaments</Gettext>
                </CategoryTitle>
              </Category>
              <Cards>
                {nearest.map(tournament => (
                  <Card
                    key={tournament.id}
                    layout="horizontal"
                    onClose={onClose}
                    onActivateTournament={this.onActivateTournament}
                    enabledStock={enabledStock}
                    {...tournament}
                  />
                ))}
              </Cards>
            </React.Fragment>
          )}
        </Scrollable>

        <Wrapper>
          <Link to="/tournaments" target="_self">
            <Gettext>All tournaments</Gettext>
          </Link>
        </Wrapper>
      </Container>
    );
  }
}

const mapStateToStocks = (state: ReduxState) => {
  const activeTab = state.tabs.tabs[state.tabs.activeTab];

  return {
    time: state.time || Date.now(),
    enabledStock: state.stocks[activeTab].enabled
  };
};

export default connect((mapStateToStocks: MapStateToProps<*, *, *>))(
  (props: SharedProps) => (
    <TournamentAPI.Consumer>
      {api => <TabContents {...props} api={api} />}
    </TournamentAPI.Consumer>
  )
);



// WEBPACK FOOTER //
// ./src/components/Tournaments/Account/TabContents.js